/**
 * Price scales, and the lines that make them.
 *
 * The rule the screen has to communicate, because the arithmetic is invisible
 * otherwise: **the most specific line in force wins.** A scale can carry a
 * discount for everything, a different one for a family, and a negotiated
 * price on one reference, all at once — and the reference's price is what
 * applies. Every hand-rolled pricing engine ships with the opposite bug first,
 * so the order is stated in the introduction and the lines are listed from
 * broadest to narrowest.
 *
 * `visible_on_portal` is here because some arrangements are internal. A
 * customer reading their own tariff conditions should not discover a line
 * somebody meant to keep between the ADV and the representative.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { download, useAction, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { money, percent } from "@hardauto/core-i18n";
import {
  Badge,
  Button,
  Card,
  DataTable,
  Input,
  Page,
  Select,
  StatusDot,
  Toolbar,
  useToast,
  type Column,
} from "@hardauto/core-ui";

import { ReferentialApi, type PriceRule, type PriceScale } from "../api";
import { NAMESPACE } from "../i18n";
import "./referential.css";

export function PricingScreen({ client }: { client: ApiClient }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new ReferentialApi(client), [client]);
  const toast = useToast();
  const action = useAction();
  const mayWrite = useCan("referential.pricing.write");

  const scalesFetcher = useCallback(() => api.listScales(), [api]);
  const scales = useResource(scalesFetcher);

  const [selected, setSelected] = useState<string | null>(null);
  const active = selected ?? scales.data?.items[0]?.code ?? null;

  const rulesFetcher = useCallback(
    () => (active ? api.listRules(active) : Promise.resolve({ items: [] })),
    [api, active],
  );
  const rules = useResource(rulesFetcher);

  const fileInput = useRef<HTMLInputElement>(null);
  const scaleInput = useRef<HTMLInputElement>(null);

  function importScales(file: File) {
    void action.run("importScales", async () => {
      try {
        const report = await api.importScales(file);
        toast.show(
          t("pricing.importScalesDone", {
            created: report.created.length,
            existing: report.existing.length,
          }),
          { tone: "success" },
        );
        scales.reload();
      } catch {
        toast.show(t("pricing.importScalesFailed"), { tone: "danger" });
      }
    });
  }

  function exportPrices() {
    void action.run("export", async () => {
      try {
        download(await api.exportPrices(), "prices.csv");
      } catch {
        toast.show(t("error.title"), { tone: "danger" });
      }
    });
  }

  function importPrices(file: File) {
    void action.run("import", async () => {
      try {
        const report = await api.importPrices(file);
        const notes = [
          t("pricing.importDone", {
            created: report.created,
            updated: report.updated,
            unchanged: report.unchanged,
          }),
        ];
        if (report.unknown_reference_count > 0) {
          notes.push(t("pricing.importUnknown", { count: report.unknown_reference_count }));
        }
        if (report.rejected_count > 0) {
          notes.push(t("pricing.importRejected", { count: report.rejected_count }));
        }
        if (report.unmatched_columns.length > 0) {
          notes.push(
            t("pricing.importUnmatched", { columns: report.unmatched_columns.join(", ") }),
          );
        }
        toast.show(notes.join(" "), {
          tone: report.unknown_reference_count + report.rejected_count > 0 ? "warning" : "success",
        });
      } catch {
        toast.show(t("pricing.importFailed"), { tone: "danger" });
      }
    });
  }

  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");

  function move(code: string, step: -1 | 1) {
    const codes = (scales.data?.items ?? []).map((scale) => scale.code);
    const at = codes.indexOf(code);
    const to = at + step;
    if (at < 0 || to < 0 || to >= codes.length) return;
    codes.splice(to, 0, ...codes.splice(at, 1));
    void action.run(`order:${code}`, async () => {
      try {
        await api.orderScales(codes);
        scales.reload();
      } catch {
        toast.show(t("error.title"), { tone: "danger" });
      }
    });
  }

  const scaleColumns: Column<PriceScale>[] = [
    {
      // First, second, third… — the order the tariffs are listed in everywhere.
      key: "order",
      header: t("pricing.column.order"),
      render: (row) => {
        const items = scales.data?.items ?? [];
        const at = items.findIndex((scale) => scale.code === row.code);
        return (
          <span>
            {at + 1}{" "}
            {mayWrite ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`${t("pricing.moveUp")} ${row.label}`}
                  disabled={at <= 0 || action.isBusy(`order:${row.code}`)}
                  onClick={() => move(row.code, -1)}
                >
                  ↑
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`${t("pricing.moveDown")} ${row.label}`}
                  disabled={at >= items.length - 1 || action.isBusy(`order:${row.code}`)}
                  onClick={() => move(row.code, 1)}
                >
                  ↓
                </Button>
              </>
            ) : null}
          </span>
        );
      },
    },
    { key: "code", header: t("pricing.column.code"), render: (row) => <code>{row.code}</code> },
    { key: "label", header: t("pricing.column.label"), render: (row) => row.label },
    {
      key: "state",
      header: t("pricing.column.state"),
      render: (row) => (
        <StatusDot
          label={row.is_active ? t("families.state.active") : t("families.state.inactive")}
          tone={row.is_active ? "success" : "neutral"}
        />
      ),
    },
    {
      // The counter tariff: what the catalogue's lists show when nobody has
      // chosen one. One at a time — choosing another demotes this one.
      key: "default",
      header: t("pricing.column.default"),
      render: (row) =>
        row.is_default ? (
          <Badge tone="info">{t("pricing.default")}</Badge>
        ) : mayWrite && row.is_active ? (
          <Button
            variant="ghost"
            size="sm"
            busy={action.isBusy(`default:${row.code}`)}
            busyLabel={t("pricing.makeDefault")}
            onClick={() => {
              void action.run(`default:${row.code}`, async () => {
                try {
                  await api.setDefaultScale(row.code);
                  scales.reload();
                } catch {
                  toast.show(t("error.title"), { tone: "danger" });
                }
              });
            }}
          >
            {t("pricing.makeDefault")}
          </Button>
        ) : null,
    },
  ];

  const ruleColumns: Column<PriceRule>[] = [
    {
      key: "family",
      header: t("pricing.column.family"),
      render: (row) =>
        row.family_code ?? <span className="hardauto-cell__muted">{t("pricing.all")}</span>,
    },
    {
      key: "article",
      header: t("pricing.column.article"),
      render: (row) =>
        row.article_reference ? (
          <span className="hardauto-ref">{row.article_reference}</span>
        ) : (
          <span className="hardauto-cell__muted">{t("pricing.all")}</span>
        ),
    },
    {
      key: "site",
      header: t("pricing.column.site"),
      render: (row) =>
        row.site_code ?? <span className="hardauto-cell__muted">{t("pricing.allSites")}</span>,
    },
    {
      key: "discount",
      header: t("pricing.column.discount"),
      render: (row) => (
        <span className="hardauto-num">{percent(row.discount_pct, i18n.language)}</span>
      ),
    },
    {
      key: "fixed",
      header: t("pricing.column.fixed"),
      render: (row) => (
        <span className="hardauto-num">
          {row.fixed_price ? money(row.fixed_price, i18n.language) : "—"}
        </span>
      ),
    },
    {
      key: "validity",
      header: t("pricing.column.validity"),
      render: (row) =>
        row.valid_from || row.valid_to ? (
          `${row.valid_from ?? "…"} → ${row.valid_to ?? "…"}`
        ) : (
          <span className="hardauto-cell__muted">{t("pricing.openEnded")}</span>
        ),
    },
    {
      key: "visible",
      header: t("pricing.column.visible"),
      render: (row) => (row.visible_on_portal ? t("pricing.yes") : t("pricing.no")),
    },
    {
      key: "actions",
      header: t("pricing.column.actions"),
      render: (row) =>
        mayWrite ? (
          <Button
            variant="ghost"
            size="sm"
            busy={action.isBusy(row.id)}
            busyLabel={t("pricing.remove")}
            onClick={() =>
              void action.run(row.id, async () => {
                try {
                  await api.deleteRule(row.id);
                  rules.reload();
                } catch {
                  toast.show(t("error.title"), { tone: "danger" });
                }
              })
            }
          >
            {t("pricing.remove")}
          </Button>
        ) : null,
    },
  ];

  return (
    <Page
      title={t("pricing.title")}
      description={t("pricing.intro")}
      actions={
        <>
          <Button
            busy={action.isBusy("export")}
            busyLabel={t("pricing.export")}
            onClick={exportPrices}
          >
            {t("pricing.export")}
          </Button>
          {mayWrite ? (
            <>
              <input
                ref={scaleInput}
                type="file"
                accept=".xlsx,.csv"
                hidden
                data-testid="scale-file"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) importScales(file);
                }}
              />
              <Button
                busy={action.isBusy("importScales")}
                busyLabel={t("pricing.importScales")}
                onClick={() => scaleInput.current?.click()}
              >
                {t("pricing.importScales")}
              </Button>
              <input
                ref={fileInput}
                type="file"
                accept=".xlsx,.csv"
                hidden
                data-testid="price-file"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) importPrices(file);
                }}
              />
              <Button
                busy={action.isBusy("import")}
                busyLabel={t("pricing.import")}
                onClick={() => fileInput.current?.click()}
              >
                {t("pricing.import")}
              </Button>
            </>
          ) : null}
          {mayWrite && !creating ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              {t("pricing.create")}
            </Button>
          ) : null}
        </>
      }
    >
      {creating ? (
        <form
          className="hardauto-inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!code.trim()) return;
            void action.run("create", async () => {
              try {
                await api.createScale({ code: code.trim(), label: label.trim() });
                setCode("");
                setLabel("");
                setCreating(false);
                scales.reload();
              } catch {
                toast.show(t("error.title"), { tone: "danger" });
              }
            });
          }}
        >
          <div className="hardauto-inline-form__field">
            <Field label={t("pricing.newCode")} required>
              {(props) => (
                <Input {...props} value={code} onChange={(event) => setCode(event.target.value)} />
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__field">
            <Field label={t("pricing.newLabel")}>
              {(props) => (
                <Input
                  {...props}
                  value={label}
                  onChange={(event) => setLabel(event.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="hardauto-inline-form__actions">
            <Button
              type="submit"
              variant="primary"
              busy={action.isBusy("create")}
              busyLabel={t("pricing.save")}
            >
              {t("pricing.save")}
            </Button>
            <Button onClick={() => setCreating(false)}>{t("pricing.cancel")}</Button>
          </div>
        </form>
      ) : null}

      <Card title={t("pricing.scaleCaption")} compactTitle flush>
        <DataTable<PriceScale>
          caption={t("pricing.scaleCaption")}
          columns={scaleColumns}
          rows={scales.data?.items ?? []}
          rowKey={(row) => row.code}
          loading={scales.loading}
          loadingLabel={t("pricing.loading")}
          error={
            scales.failed
              ? { title: t("error.title"), onRetry: scales.reload, retryLabel: t("error.retry") }
              : undefined
          }
          empty={{ title: t("pricing.empty.title"), description: t("pricing.empty.body") }}
        />
      </Card>

      {active ? (
        <>
          <Toolbar>
            <Select
              aria-label={t("pricing.scaleCaption")}
              value={active}
              onChange={(event) => setSelected(event.target.value)}
            >
              {(scales.data?.items ?? []).map((scale) => (
                <option key={scale.code} value={scale.code}>
                  {scale.label}
                </option>
              ))}
            </Select>
          </Toolbar>
          <Card title={t("pricing.ruleCaption")} compactTitle flush>
            <DataTable<PriceRule>
              caption={t("pricing.ruleCaption")}
              columns={ruleColumns}
              rows={rules.data?.items ?? []}
              rowKey={(row) => row.id}
              loading={rules.loading}
              loadingLabel={t("pricing.loading")}
              error={
                rules.failed
                  ? { title: t("error.title"), onRetry: rules.reload, retryLabel: t("error.retry") }
                  : undefined
              }
              empty={{ title: t("pricing.noRules.title"), description: t("pricing.noRules.body") }}
            />
          </Card>
        </>
      ) : null}
    </Page>
  );
}
